import * as z from 'zod';

import { zodTemporalInstant } from '../Temporal';

/**
 * TickEventTypes are the types of events that can be scheduled on a loan.
 *
 * NOTE: If removing a type, first move it down to the DeprecatedTickEventType
 * enum. If you sipmly remove one from these zod definitions and data remains
 * present in the database, loans will fail to load and users will experience
 * disruptive errors.
 */
export const TickEventType = z.enum([
  'PRINCIPAL_LATE_AUTOMATION',
  'GRACE_PAST_DUE_EMAIL',
  'GRACE_PAST_DUE',
  // 'INITIATE_AUTO_PAYMENT', // TODO https://github.com/willowservicing/willow/issues/8430
  // 'APPLY_DELINQUENT_INTEREST', // TODO https://github.com/willowservicing/willow/issues/8431
  'BORROWER_INSURANCE_EXPIRING_SOON',
  'BORROWER_INSURANCE_EXPIRED',
  'NACHA_AUTOPAY_INITIATE',

  // Workout Plans
  'FORBEARANCE_PLAN_COMPLETED',
  'FORBEARANCE_PLAN_CANCELLED',
]);
export type TickEventType = z.infer<typeof TickEventType>;

/**
 * TickEvent represents a scheduled event on a loan to be run by LoanTicker.
 *
 * Each type of tick event should correspond to a `Tickable` interface
 * implementation. These implementations are registered on the `allTickables`
 * static property of `LoanTicker`.
 *
 * Steps for adding a new tick event type:
 *
 * @todo migrate this to a cursor prompt file once the process has stabilized a
 * bit (still subject to changes)
 *
 * 1. Add your new event type to the `TickEventType` enum.
 * 2. Implement a `Tickable` class for your event type.
 *   @see {@link packages/shared-node/src/cron/loanTicker/Tickable.ts}
 *   @see {@link packages/shared-node/src/cron/loanTicker/tickables/}
 * 3. Register your new tickable implementation on the `allTickables` static
 *    property of `LoanTicker`.
 *
 * @see {@link packages/shared-node/src/cron/loanTicker/LoanTicker.ts}
 */
export const TickEvent = z.object({
  time: zodTemporalInstant,
  type: TickEventType,
});
export type TickEvent = z.infer<typeof TickEvent>;

/**
 * DeprecatedTickEventType are the types of events that are no longer supported.
 * These types should be maintained here for backwards compatibility until
 * they can be fully removed from the database. Once they're removed from the
 * database, feel free to remove them here.
 */
export const DeprecatedTickEventType = z.enum(['RETRIEVE_FAILED_TRANSACTIONS', 'REPAYMENT_PLAN_COMPLETED']);
export type DeprecatedTickEventType = z.infer<typeof DeprecatedTickEventType>;

const deprecatedTickEventTypes = new Set<string>([...DeprecatedTickEventType.options]);

export const DeprecatedTickEvent = z.object({
  time: zodTemporalInstant,
  type: DeprecatedTickEventType,
});
export type DeprecatedTickEvent = z.infer<typeof DeprecatedTickEvent>;

export const TickEventArray = z
  .array(z.union([TickEvent, DeprecatedTickEvent]))
  .transform<TickEvent[]>((value) => value.filter((v): v is TickEvent => !deprecatedTickEventTypes.has(v.type)));
export type TickEventArray = z.infer<typeof TickEventArray>;
